import React, { useState, useEffect, useRef } from 'react';

interface CanvasTextItemProps {
  item: {
    id: string;
    type: 'text';
    position: { x: number; y: number };
    zIndex?: number;
    rotation?: number;
    data: {
      text: string;
      fontSize: number;
      fontFamily: string;
      color: string;
      width: number;
      height: number;
    };
  };
  isDragging: boolean;
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>, item: any) => void;
  onClick: (e: React.MouseEvent<HTMLDivElement>, item: any) => void;
  onUpdateText: (itemId: string, newText: string) => void;
}

export const CanvasTextItem: React.FC<CanvasTextItemProps> = ({
  item,
  isDragging,
  onMouseDown,
  onClick,
  onUpdateText,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(item.data.text);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing) {
      textAreaRef.current?.focus();
      textAreaRef.current?.select();
    }
  }, [isEditing]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isEditing) {
      onMouseDown(e, item);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    onClick(e, item);
  };

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    onUpdateText(item.id, text);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
  };

  return (
    <div
      className="absolute"
      data-item-id={item.id}
      style={{
        left: 0,
        top: 0,
        width: item.data.width,
        height: item.data.height,
        zIndex: isDragging ? 999 : (item.zIndex || 1),
        transform: `rotate(${item.rotation || 0}deg) scale(${isDragging ? 0.95 : 1})`,
        transformOrigin: 'center center',
        transition: isDragging ? 'none' : 'transform 0.2s ease-out',
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {isEditing ? (
        <textarea
          ref={textAreaRef}
          value={text}
          onChange={handleTextChange}
          onBlur={handleBlur}
          style={{
            width: '100%',
            height: '100%',
            border: '1px dashed #3b82f6',
            outline: 'none',
            resize: 'none',
            backgroundColor: 'transparent',
            fontFamily: item.data.fontFamily,
            fontSize: item.data.fontSize,
            color: item.data.color,
            textAlign: 'center',
            lineHeight: '1.2',
          }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: item.data.fontFamily,
            fontSize: item.data.fontSize,
            color: item.data.color,
            textAlign: 'center',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {item.data.text}
        </div>
      )}
    </div>
  );
};
